<template>
  <div class="gmf-sp gmf-card" :style="cachedHeight ? {height: cachedHeight} : undefined">
    <template v-if="isInViewport">
      <div ref="content" class="gmf-sp-content">
        <div class="gmf-sp-action gmf-text-small">
          <Icon icon="pinned" v-if="post && post.pins && post.pins.includes('social_feed')"></Icon>  
          <div style="width: 100%; display: flex; justify-content: start; gap: 4px;" class="gmf-sp-action-text">
            <span style="flex-shrink: 0;">
              {{ customerFirstName }} {{ actionPhrase }}
            </span>
            <Link v-if="post.target && post.target.url && hasTargetName" :href="post.target.url">{{ post.target.name }}</Link>
            <span v-else-if="post.target && hasTargetName">{{ post.target.name }}</span>
          </div>
        </div>
        <div 
          class="gmf-sp-identity"
          @click="openProfileCard(post.author)"
          :class="{ 'gmf-clickable': clickable, 'gmf-skin': enabledSkins, 'gmf-dark': enabledSkins && post.author.profileSkin?.dark }"
        >
          <div v-if="enabledSkins && post.author.profileSkin" class="gmf-sp-skin" :style="{ backgroundImage: 'url('+(post.author.profileSkin?.imageUrl || post.author.profileSkin?.image_url)+')', opacity: post.author.profileSkin?.opacity }"></div>
          <Avatar
            :customer="post.author"
            class="gmf-sp-identity-avatar"
            defaultPopupTab="profile"
            disableOnlineIndicator
            disablePopout
            animated
          />
          <div v-if="clickable" class="gmf-sp-identity-icon" >
            <Icon icon="customer-profile"/>
          </div>
          <div class="gmf-sp-poster">
            <CustomerName class="gmf-sp-poster-name gmf-subheader" :customer="post.author" />
            <div v-if="post.author.flags && post.author.flags.includes('is_employee') && state.merchantConfig.widgets.showEmployeeTitle">
              <span class="gmf-customer-title" v-text="t('comments.employee_title')"></span>
            </div>
            <CustomerLevelRow :customer="post.author" v-else />
            <CustomerAttributePills v-if="customerAttributes" :attributes="customerAttributes" location="comment" :maxLines="1" />
          </div>
        </div>

        <div class="gmf-sp-body">
          <div class="gmf-subheader" v-if="title && post.type.name !== 'marketplace'">{{ title }}</div>

          <div class="gmf-comment-text" v-if="text && text.length > 0">
            <CommentText :value="text"></CommentText>
          </div>

          <Poll v-for="poll of polls" :key="poll.uuid" :poll="poll" :showTranslate="translate"></Poll>

          <div v-if="shouldTranslate" class="gmf-translate-btn">
            <a class="gmf-link" @click="translate = !translate" href="#">{{ !translate ?
              t('comments.translations.translate') : t('comments.translations.show_original') }}</a>
          </div>

          <div
            v-if="guidelineLabel"
            class="gmf-alert gmf-alert-warn"
          >
            <span v-html="guidelineLabel" />
            <a v-if="t('comments.guidelines.marketplace.text')" class="gmf-alert-link gmf-ml-2" @click="openInfoPageModal(
              'comments.guidelines.marketplace.title',
              'comments.guidelines.marketplace.text'
            )">{{ t('read_more') }}</a>
          </div>

          <div class="gmf-comment-pros-and-cons gmf-mt-3" v-if="hasProsOrCons">
            <div class="gmf-comment-pros" v-if="pros && pros.length > 0">
              <div class="gmf-comment-pc" v-for="pro in pros" :key="pro">
                <Icon icon="pro" />
                <div class="gmf-comment-pc-text" v-text="pro"></div>
              </div>
            </div>
            <div class="gmf-comment-cons" v-if="cons && cons.length > 0">
              <div class="gmf-comment-pc" v-for="con in cons" :key="con">
                <Icon icon="con" />
                <div class="gmf-comment-pc-text" v-text="con"></div>
              </div>
            </div>
          </div>

          <div class="gmf-sp-rating-wrap">
            <RatingStars v-if="post.content.ratings.product_score" type="product_score" :rating="post.content.ratings.product_score.value" />
            <RatingStars v-else-if="post.content.ratings.hype_factor" type="hype_factor" :rating="post.content.ratings.hype_factor.value" />
            <RatingStars v-else-if="post.content.ratings.site_score" type="site_score" :rating="post.content.ratings.site_score.value" />
            <span v-if="post.isVerified" class="gmf-comment-verification gmf-text-small"><div class="gmf-comment-verification-icon"><Icon icon="verified-comment-checkmark" /></div>{{t('comments.verified_buyer')}}</span>
          </div>
        </div>

        <ImageGrid v-if="elementsToShow.images && post.content.images && post.content.images.length > 0" :images="post.content.images" :commentId="post.id" />
        <CommentImageGallery v-if="elementsToShow.uploads && post.content.images && post.content.images.length > 1" :images="post.content.images" :clickHandler="v => $emit('selectedIndex', v)"/>

        <div class="gmf-sp-marketplace-footer" v-if="post.type.name === 'marketplace'">
          <div class="gmf-sp-marketplace-footer-content-col">
            <div class="gmf-sp-marketplace-footer-content-top" v-if="post.meta">
              <div class="gmf-text-small gmf-sp-marketplace-footer-price" v-if="post.meta.price">{{ post.meta.price }}</div>
              <div class="gmf-text-small" v-if="post.meta.location">{{ post.meta.location }}</div>
            </div>
            <div class="gmf-sp-marketplace-footer-content-bottom" v-if="post.content.title">{{ post.content.title }}</div>
          </div>
          <div class="gmf-sp-marketplace-footer-right-col" v-if="state.merchantConfig.directMessages.enabled">
            <div class="gmf-btn gmf-btn-sm" @click="openConversation">{{ t('comments.direct_message') }}</div>
          </div>
        </div>

        <div class="gmf-sp-linked-products-container" v-if="linkedProducts && linkedProducts.length > 0">
          <div class="gmf-subheader" v-text="t('comments.linked_products_header')"></div>
          <div class="gmf-sp-linked-products-list">
            <Link class="gmf-sp-linked-product" v-for="product in linkedProducts" :key="product.id" :href="product.url">
              <div class="gmf-sp-linked-product-image" :style="{backgroundImage: 'url(' + product.imageUrl + ')'}"></div>
              <div class="gmf-sp-linked-product-title">{{ product.name }}</div>
            </Link>
          </div>
        </div>

        <div class="gmf-sp-stats">
          <div class="gmf-text-small"><span v-if="hasReactions" class="gmf-sp-reaction-container">
            <Icon class="gmf-sp-reaction" :class="'gmf-sp-reaction-' + reactionIcon" :icon="reactionIcon" />
            <span>{{ votes.score }}</span>
          </span></div><!--@TODO up/downvotes-->
          <div>
            <div class="gmf-sp-poster-date gmf-text-small" v-text="date" v-if="!hideDates"></div>
          </div>
          <!--<div class="gmf-text-small" v-if="post.replyCount > 0">{{ post.replyCount }} {{ t('social_feed.x_comments') }}</div> -->
        </div>

        <div class="gmf-sp-foot" v-if="isLinkedPost">
          <div class="gmf-text-small">{{ originatedFrom }}</div>
        </div>
        <div class="gmf-sp-foot" v-else>
          <CommentVoteButtons :comment="post" @update:votes="v => $emit('update:votes', v)">
            <template v-slot:like-text><span>{{ t('social_feed.like_btn_text') }}</span></template>
          </CommentVoteButtons>
          <button class="gmf-comment-footer-btn" v-if="canReply" @click="reply">
            <Icon icon="reply" class="gmf-comment-reply-icon"/> {{ t('social_feed.comment_btn_text') }}
          </button>
          <button class="gmf-comment-footer-btn" @click="openCommentActionsModal(post)">
            <Icon icon="more-menu" />
            {{ t('comment_actions.more_button.label') }}
          </button>
        </div>
        <CommentReplyForm
          v-if="showReplyForm"
          :depth="0"
          :finalNode="true"
          :parentBranches="[]"
          :commentType="post.type.name"
          :targetId="post.target && post.target.id"
          :parentId="post.id"
        />
        <div v-if="post.replies && post.replies.length > 0">
          <Comment
            v-for="comment in post.replies"
            @loadReplies="id => $emit('loadReplies', id)"
            :comment="comment"
            :key="comment.id"
            :voteMode="voteMode"
            :depth="0"
            :elements="replyElementsToShow"
            :inPostModal="inPostModal"
            @selectedComment="(comment, index) => $emit('selectedComment', comment, index)"
          />
          <!-- @TODO Load spinner -->
          <a 
            href="#" 
            v-if="post.replyCount > post.replies.length" 
            @click.prevent="handleLoadReplies(post.id)" 
            style="font-weight: bold; margin-top: var(--gmf-p-3); display: block;"
          >
            Visa {{ post.replyCount - post.replies.length }} kommentarer till ...
          </a>
        </div>
      </div>
    </template>
  </div>
</template>

<style>
.gmf-card.gmf-sp {
  padding: var(--gmf-p-3) var(--gmf-p-4);
  margin-bottom: var(--gmf-p-4);
}

.gmf-sp-identity {
  display: flex;
  align-items: center;
  margin-left: -16px;
  margin-right: -16px;
  padding: 0px 16px;
  padding-left: 10px;
  position: relative;
  border-top: 1px solid var(--gmf-col-lightgray);
  border-bottom: 1px solid var(--gmf-col-lightgray);
  min-height: 75px;
}
.gmf-sp-identity.gmf-skin {
  background: white;
}
.gmf-sp-identity.gmf-skin.gmf-dark {
  background: black;
}
.gmf-sp-identity.gmf-skin.gmf-dark .gmf-link, .gmf-sp-identity.gmf-skin.gmf-dark .gmf-customer-name {
  color: var(--gmf-col-white);
}
.gmf-sp-identity.gmf-skin.gmf-dark .gmf-level-row-level {
  color: var(--gmf-col-white);
}
.gmf-sp-skin {
  position: absolute;
  background-size: 700px;
  background-position: center;
  inset: 0;
}

.gmf-sp-skin.gmf-dark .gmf-level-row-level {
  color: var(--gmf-col-white);
}
.gmf-sp-skin.gmf-dark .gmf-link {
  color: var(--gmf-col-white);
}

.gmf-sp-identity-avatar {
  margin-right: var(--gmf-p-1);
  position: relative;
}
.gmf-sp-identity-avatar.gmf-avatar {
  width: 64px;
  height: 64px;
  position: absolute;
}
.gmf-sp-identity-avatar.gmf-avatar-3d {
  width: 96px;
  height: 90px;
  line-height: 0;
  background: none;
  position: absolute;
  bottom: 0;
  margin-left: -10px;
  border-radius: 0px;
  overflow: hidden;
}
.gmf-sp-identity:has(.gmf-avatar-3d) .gmf-sp-identity-icon {
  left: 64px;
  transform: translateY(50%);
}
.gmf-sp-identity .gmf-avatar-3d .gmf-avatar-content {
  border-radius: 0 !important;
  height: 96px;
}
.gmf-sp-body {
  margin-top: var(--gmf-p-4);
  margin-bottom: var(--gmf-p-3);
}
.gmf-sp-rating-wrap {
  line-height: 0;
  margin-top: var(--gmf-p-3);
  display: flex;
  gap: var(--gmf-p-3);
  align-items: center;
}
.gmf-sp-rating-wrap .gmf-comment-verification {
  margin: 0 !important;
}
.gmf-sp-foot {
  border-top: 1px solid var(--gmf-col-lightgray);
  padding-top: var(--gmf-p-3);

  display: flex;
  justify-content: space-evenly;
  touch-action: manipulation;
}
.gmf-sp-foot:not(:last-child) {
  border-bottom: 1px solid var(--gmf-col-lightgray);
  margin-bottom: var(--gmf-object-spacing);
  padding-bottom: var(--gmf-p-3);
}
.gmf-sp-identity-icon {
  position: absolute;
  left: 54px;
  bottom: 0;
  z-index: 1;
  padding: var(--gmf-p-2);
  background: var(--gmf-col-white);
  border-radius: 100%;
  display: flex;
  align-items: center;
}
.gmf-sp-identity-icon .gmf-icon {
  width: 16px;
  height: 16px;
}
.gmf-sp-poster {
  position: relative;
  flex-grow: 1;
  z-index: 1;
  overflow: hidden;
  margin-left: 82px;
  display: flex;
  flex-direction: column;
  gap: calc(var(--gmf-p-2) - var(--gmf-p-1));
  justify-content: center;
  padding: var(--gmf-p-1) 0;
}
.gmf-sp-poster .gmf-subheader {
  margin-bottom: 0;
}
.gmf-sp-poster-name {
  line-height: 1;
}
.gmf-sp-poster-name.gmf-subheader.gmf-link-disabled {
  color: var(--gmf-col-darkgray);
}
.gmf-dark .gmf-sp-poster-name.gmf-subheader.gmf-link-disabled {
  color: var(--gmf-col-white);
}
.gmf-sp-stats {
  display: flex;
  justify-content: space-between;
  margin-top: var(--gmf-p-3);
  margin-bottom: var(--gmf-p-3);
  min-height: 16px;
}
.gmf-sp-action {
  padding-bottom: var(--gmf-p-4);
  display: flex;
  align-items: center;
  white-space: pre-wrap;
  word-break: break-word;
}
.gmf-sp-action a {
  text-decoration: none;
  font-weight: bold;
}
.gmf-sp-reaction-container {
  display: flex;
  align-items: center;
}
.gmf-sp-reaction {
  display: inline-block;
  height: 16px;
  width: 16px;
  margin-right: 3px;
}
.gmf-sp-reaction-upvote {
  --gmf-icon-color: var(--gmf-col-upvote);
}
.gmf-sp-reaction-downvote {
  --gmf-icon-color: var(--gmf-col-downvote);
}
.gmf-sp-reaction-like {
  --gmf-icon-color: var(--gmf-col-like);
}


.gmf-sp-target-image {
  height: 48px;
  width: 48px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}
.gmf-sp-foot .gmf-comment-footer-btn {
  color: var(--gmf-col-darkgray);
}

.gmf-sp-marketplace-footer-content-col {
  gap: var(--gmf-p-2);
  display: flex;
  flex-direction: column;
}
.gmf-context .gmf-sp-marketplace-footer {
  display: flex;
  justify-content: space-between;
  padding: var(--gmf-p-4) var(--gmf-p-4);
  background: var(--gmf-col-nearblack);
  align-items: center;
  gap: var(--gmf-p-2);
  margin-left: calc(var(--gmf-p-4)*-1);
  width: calc(var(--gmf-p-4)*2 + 100%);
}
.gmf-sp-marketplace-footer-content-top {
  display: flex;
  gap: var(--gmf-p-3);
}
.gmf-context .gmf-sp-marketplace-footer-price {
  font-weight: bold;
  color: var(--gmf-col-white) !important;
}
.gmf-sp-marketplace-footer-content-bottom {
  font-weight: bold !important;
  color: var(--gmf-col-white) !important;
}
.gmf-sp-marketplace-footer .gmf-btn {
  background: var(--gmf-col-darkgray);
  color: var(--gmf-col-white);
}

/* Reply styling */
.gmf-sp .gmf-comment-customer-col {
    width: 48px;
}
.gmf-sp .gmf-comment-stem,
.gmf-sp .gmf-comment-indent {
  margin-left: 23px;
}

.gmf-sp-action .gmf-icon {
  width: 15px;
  height: 15px;
  margin-right: 2px;
  flex-shrink: 0;
}

.gmf-sp-action.gmf-text-small {
  gap: var(--gmf-p-1);
}

.gmf-translate-btn {
  margin-top: var(--gmf-p-4); 
  margin-bottom: var(--gmf-p-3);
}

.gmf-sp-linked-products-container {
  margin-bottom: var(--gmf-p-4);
  margin-top: var(--gmf-p-4);
}
.gmf-sp-linked-products-container .gmf-subheader {
  margin-bottom: var(--gmf-p-3);
}

.gmf-sp-linked-products-list {
  display: flex;
  gap: var(--gmf-p-3);
  flex-direction: column;
}
.gmf-sp-linked-product {
  display: flex;
  gap: var(--gmf-p-3);
  align-items: center;
  text-decoration: none;
  border-radius: var(--gmf-rounding);
}
.gmf-sp-linked-product-title {
  word-break: break-word;
  margin-right: var(--gmf-p-3);
}
.gmf-sp-linked-product:hover .gmf-sp-linked-product-title {
  text-decoration: underline;
}
.gmf-sp-linked-product .gmf-btn {
  flex-shrink: 0;
  flex-grow: 0;
}
.gmf-sp-linked-product-image {
  height: 60px;
  width: 60px;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: min(4px, var(--gmf-rounding));
}

.gmf-sp-action-text * {
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;
  white-space: nowrap;
}

.gmf-sp-content {
  position: relative;
  width: 100%;
}

.gmf-sp-more-btn {
  background: none !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  cursor: pointer;
  display: block;
  line-height: 0 !important;
  margin-top: -4px !important;
  margin-bottom: -4px !important;
}
.gmf-sp-more-btn .gmf-icon {
  margin: 0 !important;
  width: 20px;
  height: 20px;
}

</style>

<script>
import md from '@/utils/md';
import state from '@/state';
import timeAgo from '@/utils/timeAgo';
import advancedString from '@/utils/advancedString';

import Poll from '@/components/Poll';
import Link from '@/components/Link';
import Icon from '@/components/Icon';
import Avatar from '@/components/Avatar';
import Comment from '@/components/Comment';
import ImageGrid from '@/components/ImageGrid';
import CustomerName from '@/components/CustomerName';
import CustomerLevelRow from '@/components/CustomerLevelRow';
import CommentReplyForm from '@/components/Comment/CommentReplyForm.vue';
import CommentVoteButtons from '@/components/CommentVoteButtons.vue';
import CommentImageGallery from '@/components/Comment/CommentImageGallery.vue';

import CustomerAttributePills from '@/components/CustomerAttributePills';
import CommentText from '@/components/Comment/CommentText';
import tooltip from '@/utils/tooltip';
import requestLogin from '@/utils/requestLogin';
import openConversation from '@/components/DirectMessages/openConversation';
import RatingStars from '@/components/RatingStars';

import openInfoPageModal from '@/components/InfoPage/openInfoPageModal';
import openPopupProfile from '@/components/PopupProfileCard/openPopupProfileCard';

import openCommentActionsModal from '@/components/CommentActionsModal/openCommentActionsModal';

const t = state.t;

export default {
  components: {
    Poll,
    Link,
    Icon,
    Avatar,
    Comment,
    ImageGrid,
    CustomerName,
    CustomerLevelRow,
    CommentReplyForm,
    CommentVoteButtons,
    CustomerAttributePills,
    CommentText,
    RatingStars,
    CommentImageGallery,
  },
  props: [
    'post',
    'elements',
    'inPostModal',
    'intersectionObserverRoot',
    'useInViewport',
  ],
  emits: ['loadReplies', 'update:votes', 'selectedIndex', 'selectedComment'],
  data () {
    return {
      state,
      showReplyForm: false,
      translate: false,
      isInViewport: true,
      cachedHeight: null,
      resizeObserver: null,
      intersectionObserver: null,
    };
  },
  methods: {
    t,
    openCommentActionsModal,
    openInfoPageModal,
    openConversation (ev) {
      if (! this.profile) {
        tooltip.create({ x: ev.pageX, y: ev.pageY }, t('direct_messages.must_be_logged_in'));
        return;
      }
      if (this.profile && this.profile.id === this.post.author.id) {
        tooltip.create({ x: ev.pageX, y: ev.pageY }, t('direct_messages.cannot_start_conversation_with_yourself'));
        return;
      }
      openConversation(this.post.author.id);
    },
    reply (ev) {
      if (!this.profile) {
        if (! requestLogin()) {
          tooltip.create({ x: ev.pageX, y: ev.pageY }, t('comments.log_in_to_write_a.product_comment'));
        }
      } else {
        this.showReplyForm = true;
      }
    },
    openProfileCard (user) {
      if (this.clickable) {
        openPopupProfile(user.id);
      }
    },
    handleLoadReplies(postId) {
      this.cachedHeight = null;
      this.$emit('loadReplies', postId);
    },
    cacheHeight() {
      if (this.$el) {
        this.cachedHeight = this.$el.offsetHeight + 'px';
      }
    },
    clearCachedHeight() {
      this.cachedHeight = null;
    },
    isManuallyInViewport() {
      if (!this.$el) return false;
      
      const rect = this.$el.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      // Check if at least part of the element is in the viewport
      const verticallyVisible = rect.top < windowHeight && rect.bottom > 0;
      return verticallyVisible;
    },
  },
  computed: {
    elementsToShow () {
      const toShow = {
        images: true,
        uploads: false,
      };
      if (this.elements) {
        for (const key in this.elements) {
          toShow[key] = this.elements[key];
        }
      }

      return toShow;
    },
    linkedProducts () {
      const products = [];

      const p = this.post.target;

      if (p && p.type === 'product' && p.name && p.name !== '<N/A>') {
        products.push(p);
      }
      if (this.post.linkedProducts) {
        products.push(...this.post.linkedProducts);
      }
      return products;
    },
    originatedFrom () {
      return advancedString(t('comments.originated_from'), {
        merchantName: this.post.merchant.name,
      });
    },
    isLinkedPost () {
      // Returns true if the post belongs to a different merchant
      return this.post.merchant && this.post.merchant.id != this.state.merchantId
    },
    guidelineLabel () {
      return md((t('comments.guidelines.' + this.post.type.name + '.comment_label')||'').replaceAll('\\n', '\n'));
    },
    customerAttributes () {
      if (! this.post.author.attributesJson) {
        return null;
      }
      return JSON.parse(this.post.author.attributesJson);
    },
    votes () {
      return this.post.votes;
    },
    polls () {
      return Object.values(this.post.polls);
    },
    hasProsOrCons () {
      return (this.pros && this.pros.length > 0) || (this.cons && this.cons.length > 0);
    },
    popupProfileCardEnabled () {
      return !!this.state.merchantConfig?.popupProfileCard?.enabled;
    },
    clickable() {
      const customer = this.post.author;
      if (customer.merchantId && customer.merchantId !== state.merchantId) {
        return false;
      }
      return this.popupProfileCardEnabled && !customer.isGuest;
    },
    reactionIcon () {
      if (this.voteMode === 'up_down') {
        return 'upvote';
      }
      return 'like';
    },
    hasReactions () {
      if (this.voteMode === 'disabled') {
        return false;
      }
      return this.votes.observerVote < 0 || this.votes.observerVote > 0 || this.votes.score > 0 || this.votes.score < 0;
    },
    replyElementsToShow () {
      return {
        title: true,
        uploads: true,
        prosAndCons: true,
        action: false,
        productImage: false,
        targetImage: false,
        target: false,
        footer: true,
      };
    },
    title () {
      if (this.post && this.post?.translation && this.translate) {
        return this.post.translation.title;
      }

      return this.post.content.title;
    },
    text () {
      if (this.post && this.post?.translation && this.translate) {
        return this.post.translation.text;
      }

      return this.post.content.text;
    },
    pros() {
      if (this.post && this.post?.translation && this.translate) {
        return this.post.translation.pros;
      }

      return this.post.content.pros;
    },
    cons() {
      if (this.post && this.post?.translation && this.translate) {
        return this.post.translation.cons;
      }

      return this.post.content.cons;
    },
    targetImageUrl () {
      if (this.post.target && this.post.target.imageUrl) {
        return this.post.target.imageUrl;
      }
      return null;
    },
    hasTargetName () {
      const target = this.post.target;
      return target && target.name && target.name !== '<N/A>';
    },
    actionPhrase () {
      if (! this.hasTargetName) {
        return t('comments.action_phrase_product.' + this.post.type.name) || t('comments.action_phrase_product.product_comment');
      }
      
      return t('comments.action_phrase.' + this.post.type.name) || t('comments.action_phrase.product_comment');
    },
    pluralizedLikes () {
      if (Math.abs(this.votes.score) === 1) {
        return t('comments.like');
      }
      return t('comments.likes');
    },
    voteMode () {
      return this.state.merchantConfig.voteMode;
    },
    commentTypeConfig () {
      return this.state.merchantConfig.comments[this.post.type.name] || {};
    },
    canReply () {
      return this.commentTypeConfig.allowReplies;
    },
    date () {
      let d = new Date(this.post.createdAt);
      return timeAgo(d.getTime()/1000);
    },
    customerFirstName () {
      const author = this.post.author;
      if (author.firstName) {
        return author.firstName;
      }
      return t('comments.anonymous');
    },
    profile() {
      return this.state.profile;
    },
    translateMode(){
     return this.state.merchantConfig.widgets?.translations?.mode; 
    },
    shouldTranslate() {
      if (this.translateMode === 'translateNothing') {
        return false;
      }
      
      if (this.post.language === 'und') {
        return false;
      }

      if (!this.post.translation) {
        return false;
      }

      return this.post.translation.language !== this.post.language;
    },
    enabledSkins() {
      return state.merchantConfig.widgets?.skins?.enabled;
    },
    hideDates() {
      return this.state.merchantConfig?.widgets?.comments?.hideDates;
    },
  }, 
  watch: {
    isInViewport(newValue) {
      if (newValue) {
        // Coming into view - clear cached height after a tick
        setTimeout(() => {
          this.clearCachedHeight();
        }, 0);
      } else if (! this.cachedHeight) {
        // Do a manual backup check to see if the post is visible
        if (this.isManuallyInViewport()) {
          // Element is actually visible despite what IntersectionObserver says
          this.isInViewport = true;
          return;
        }
        
        // Going out of view - cache height
        this.cacheHeight();
      }
    },
    // Watch for changes in post content or replies that would affect height
    'post.replies.length'() {
      if (this.isInViewport) {
        this.clearCachedHeight();
      }
    },
    'post.content'() {
      if (this.isInViewport) {
        this.clearCachedHeight();
      }
    },
    showReplyForm() {
      if (this.isInViewport) {
        this.clearCachedHeight();
      }
    }
  },
  mounted() {
    this.translate = this.state.merchantConfig.widgets?.translations?.mode === 'autoTranslateAndOriginalButton';

    if (this.useInViewport) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.isInViewport = entries[0].isIntersecting;
        },
        {
          rootMargin: '100px 0px', // Increased margin to load earlier
          root: this.intersectionObserverRoot,
          threshold: 0 // Add threshold to detect partial visibility
        }
      );

      this.intersectionObserver.observe(this.$el);
    }
  },
  beforeUnmount() {
    // Cleanup
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
};
</script>
